import { Address } from '@adornis/address/db/address.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { MailTemplateVars } from '@adornis/bh-matching-mails/entities/mail-templates.js';
import { DefaultMenteeUser, DefaultMentorUser, Gender } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { validate } from '@adornis/validation/decorators.js';
import { and } from '@adornis/validation/functions/and.js';
import { minLength } from '@adornis/validation/functions/length.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { max, min } from '@adornis/validation/functions/range.js';

/**
 * ANLEITUNG:
 * @Markus
 * - enums müssen immer den Namen und Identifier haben
 * - enums müssen registriert werden  registerEnum({ enum: ConfirmEnum, name: 'ConfirmEnum' });
 * - Felder hinzufügen, löschen erstmal möglich
 * - Validation löschen ist kein Problem
 * ! Validation hinzufügen, muss eine Migration vorgenommen werden
 *
 * 1. Felder anlegen
 * 2. Translation in workspaces/mentoring-uni-frankfurt/src/translations.ts einfügen
 * 3. Felder müssen in workspaces/mentoring-uni-frankfurt/src/client/mentoring-ffm-signup.ts (Mentee) bzw. workspaces/mentoring-uni-frankfurt/src/client/pages/mentoring-mentor-sigup.ts (Mentor) hinzugefügt werden, damit es dann an der richtigen Stelle erscheint
 *
 */

export enum ConfirmOrKeinEnum {
  JA = 'JA',
  NEIN = 'NEIN',
  KEIN = 'KEIN',
}

export enum ConfirmEnum {
  JA = 'JA',
  NEIN = 'NEIN',
}

export enum MeetingType {
  PRESENCE = 'PRESENCE',
  ONLINE = 'ONLINE',
  BOTH = 'BOTH',
}

export enum FirstTimeEnum {
  JA = 'JA',
  NEIN = 'NEIN',
  NEIN_MORE_THAN_2 = 'NEIN_MORE_THAN_2',
}

export enum NeededFromMentorEnum {
  BEGLEITUNG_WAHRENDES_STUDIUMS = 'BEGLEITUNG_WAHRENDES_STUDIUMS',
  WEGE_IN_DIE_WISSENSCHAFT = 'WEGE_IN_DIE_WISSENSCHAFT',
  ZUGANG_ZU_WIRTSCHAFTSUNTERNEHMEN = 'ZUGANG_ZU_WIRTSCHAFTSUNTERNEHMEN',
  WEGE_IN_DIE_SELBSTSTAEDIGKEIT = 'WEGE_IN_DIE_SELBSTSTAEDIGKEIT',
  BERATUNG_ZU_FUHRUNGSTHEMEN = 'BERATUNG_ZU_FUHRUNGSTHEMEN',
  BERATUNG_ZU_KARRIERWEGEN = 'BERATUNG_ZU_KARRIERWEGEN',
  BERATUNG_ZU_KARRIERSTRATEGIEN = 'BERATUNG_ZU_KARRIERSTRATEGIEN',
  BERATUNG_ZU_VEREINBARKEIT_VOR_BERUF_UNTERNEHMEN = 'BERATUNG_ZU_VEREINBARKEIT_VOR_BERUF_UNTERNEHMEN',
  AUF_UND_AUSBAU_EINS_NETZWEITES = 'AUF_UND_AUSBAU_EINS_NETZWEITES',
  ROLLE_AUF_DIE_FRAU_IN_MAENNERDOMINIERTEM_UMFELD_KLAEREN = 'ROLLE_AUF_DIE_FRAU_IN_MAENNERDOMINIERTEM_UMFELD_KLAEREN',
  PERSOENLICHKEITENKOMPETENZEN_ENTWICKELN = 'PERSOENLICHKEITENKOMPETENZEN_ENTWICKELN',
  SONSTIGES = 'SONSTIGES',
}

// FB00 Lehramt
// FB01 Rechtswissenschaft
// FB02 Wirtschaftswissenschaften
// FB03 Gesellschaftswissenschaften
// FB04 Erziehungswissenschaften
// FB05 Psychologie und Sportwissenschaften
// FB06 Evangelische Theologie
// FB07 Katholische Theologie
// FB08 Philosophie und Geschichtswissenschaften
// FB09 Sprach- und Kulturwissenschaften
// FB10 Neuere Philologien
// FB11 Geowissenschaften / Geographie
// FB12 Informatik und Mathematik
// FB13 Physik
// FB14 Biochemie, Chemie und Pharmazie
// FB15 Biowissenschaften
// FB16 Medizin
export enum FachbereichEnum {
  FB00 = 'FB00',
  FB01 = 'FB01',
  FB02 = 'FB02',
  FB03 = 'FB03',
  FB04 = 'FB04',
  FB05 = 'FB05',
  FB06 = 'FB06',
  FB07 = 'FB07',
  FB08 = 'FB08',
  FB09 = 'FB09',
  FB10 = 'FB10',
  FB11 = 'FB11',
  FB12 = 'FB12',
  FB13 = 'FB13',
  FB14 = 'FB14',
  FB15 = 'FB15',
  FB16 = 'FB16',
}

export enum HauptFaecherEnum {
  Afrikanistik = 'Afrikanistik',
  AllgundverglLiteraturwissenschaft = 'AllgundverglLiteraturwissenschaft',
  AmericanStudies = 'AmericanStudies',
  AnglophoneLiteraturesCulturesandMedia = 'AnglophoneLiteraturesCulturesandMedia',
  Archaologie = 'Archaologie',
  Archaometrie = 'Archaometrie',
  AudiovisualandCinemaStudies = 'AudiovisualandCinemaStudies',
  Betriebswirtschaftslehre = 'Betriebswirtschaftslehre',
  Biochemie = 'Biochemie',
  Bioinformatik = 'Bioinformatik',
  BiologieLehramt = 'BiologieLehramt',
  Biophysik = 'Biophysik',
  Biowissenschaften = 'Biowissenschaften',
  Chemie = 'Chemie',
  ChemieLehramt = 'ChemieLehramt',
  ComparativeLiteratures = 'ComparativeLiteratures',
  CuratorialStudiesTheorieGeschichteKritik = 'CuratorialStudiesTheorieGeschichteKritik',
  DeutschLehramt = 'DeutschLehramt',
  DeutschPortugiesischeStudienEstudosLusoAlemaes = 'DeutschPortugiesischeStudienEstudosLusoAlemaes',
  DeutscheLiteratur = 'DeutscheLiteratur',
  Dramaturgie = 'Dramaturgie',
  EmpirischeSprachwissenschaften = 'EmpirischeSprachwissenschaften',
  EnglischLehramt = 'EnglischLehramt',
  EnglishStudies = 'EnglishStudies',
  ErdkundeLehramt = 'ErdkundeLehramt',
  Erziehungswissenschaften = 'Erziehungswissenschaften',
  Ethnologie = 'Ethnologie',
  EvangelischeReligionLehramt = 'EvangelischeReligionLehramt',
  EvangelischeTheologie = 'EvangelischeTheologie',
  FilmkulturArchivierungProgrammierungPrasentation = 'FilmkulturArchivierungProgrammierungPrasentation',
  FranzosischLehramt = 'FranzosischLehramt',
  GenderStudies = 'GenderStudies',
  Geographie = 'Geographie',
  GeographienderGlobalisierungMarkteundMetropolen = 'GeographienderGlobalisierungMarkteundMetropolen',
  Geowissenschaften = 'Geowissenschaften',
  Germanistik = 'Germanistik',
  Geschichte = 'Geschichte',
  GeschichteLehramt = 'GeschichteLehramt',
  GeschichteundPhilosophiederWissenschaften = 'GeschichteundPhilosophiederWissenschaften',
  GriechischLehramt = 'GriechischLehramt',
  GriechischePhilologie = 'GriechischePhilologie',
  Informatik = 'Informatik',
  InformatikLehramt = 'InformatikLehramt',
  InterdisciplinaryNeuroscience = 'InterdisciplinaryNeuroscience',
  InternationalEconomicsandEconomicPolicy = 'InternationalEconomicsandEconomicPolicy',
  InternationalManagement = 'InternationalManagement',
  InternationaleStudienFriedensundKonfliktforschung = 'InternationaleStudienFriedensundKonfliktforschung',
  IslamischeReligionLehramt = 'IslamischeReligionLehramt',
  IslamischeStudien = 'IslamischeStudien',
  ItalienischLehramt = 'ItalienischLehramt',
  ItalienstudienStudiItaliani = 'ItalienstudienStudiItaliani',
  JapaninderWeltGlobaleHerausforderungenundkulturellePerspektiven = 'JapaninderWeltGlobaleHerausforderungenundkulturellePerspektiven',
  Japanologie = 'Japanologie',
  Judaistik = 'Judaistik',
  KatholischeReligionLehramt = 'KatholischeReligionLehramt',
  KatholischeTheologie = 'KatholischeTheologie',
  Koreastudien = 'Koreastudien',
  KulturanthropologieundEuropaischeEthnologie = 'KulturanthropologieundEuropaischeEthnologie',
  KunstLehramt = 'KunstLehramt',
  KunstMedienKulturelleBildung = 'KunstMedienKulturelleBildung',
  Kunstgeschichte = 'Kunstgeschichte',
  LateinLehramt = 'LateinLehramt',
  LateinischePhilologie = 'LateinischePhilologie',
  Linguistics = 'Linguistics',
  Linguistik = 'Linguistik',
  Mathematik = 'Mathematik',
  MathematikLehramt = 'MathematikLehramt',
  Medizin = 'Medizin',
  Medizintechnik = 'Medizintechnik',
  Meteorologie = 'Meteorologie',
  ModernEastAsianStudies = 'ModernEastAsianStudies',
  MolekulareBiotechnologie = 'MolekulareBiotechnologie',
  MolekulareBiowissenschaften = 'MolekulareBiowissenschaften',
  MolekulareMedizin = 'MolekulareMedizin',
  MoneyandFinance = 'MoneyandFinance',
  MovingCulturesTransculturalEncounters = 'MovingCulturesTransculturalEncounters',
  MusikLehramt = 'MusikLehramt',
  Musikwissenschaft = 'Musikwissenschaft',
  Pharmazie = 'Pharmazie',
  Philosophie = 'Philosophie',
  PhilosophieLehramt = 'PhilosophieLehramt',
  PhysicalBiologyofCellsandCellInteractions = 'PhysicalBiologyofCellsandCellInteractions',
  Physik = 'Physik',
  PhysikLehramt = 'PhysikLehramt',
  PhysischeGeographie = 'PhysischeGeographie',
  PolitikundWirtschaftLehramt = 'PolitikundWirtschaftLehramt',
  Politikwissenschaft = 'Politikwissenschaft',
  PolitischeTheorie = 'PolitischeTheorie',
  Psychologie = 'Psychologie',
  Rechtswissenschaft = 'Rechtswissenschaft',
  Religionsphilosophie = 'Religionsphilosophie',
  Religionswissenschaft = 'Religionswissenschaft',
  ReligionswissenschaftIslamischeReligion = 'ReligionswissenschaftIslamischeReligion',
  ReligionswissenschaftVergleichendeReligionswissenschaft = 'ReligionswissenschaftVergleichendeReligionswissenschaft',
  Romanistik = 'Romanistik',
  RomanistischeLinguistik = 'RomanistischeLinguistik',
  RomanistischeLiteraturundKulturwissenschaft = 'RomanistischeLiteraturundKulturwissenschaft',
  SachunterrichtLehramt = 'SachunterrichtLehramt',
  Sinologie = 'Sinologie',
  Skandinavistik = 'Skandinavistik',
  SoutheastAsianStudies = 'SoutheastAsianStudies',
  SozialwissenschaftendesSports = 'SozialwissenschaftendesSports',
  Soziologie = 'Soziologie',
  SpanischLehramt = 'SpanischLehramt',
  SportLehramt = 'SportLehramt',
  SportmedizinischesTrainingLeistungsphysiologie = 'SportmedizinischesTrainingLeistungsphysiologie',
  Sportwissenschaft = 'Sportwissenschaft',
  SprachenundKulturenSudostasiens = 'SprachenundKulturenSudostasiens',
  TheaterFilmuMedienwissenschaft = 'TheaterFilmuMedienwissenschaft',
  Umweltwissenschaften = 'Umweltwissenschaften',
  VoruFruhgeschichtlicheArchaologie = 'VoruFruhgeschichtlicheArchaologie',
  WirtschaftsundFinanzsoziologie = 'WirtschaftsundFinanzsoziologie',
  Wirtschaftsinformatik = 'Wirtschaftsinformatik',
  Wirtschaftspadagogik = 'Wirtschaftspadagogik',
  Wirtschaftssoziologie = 'Wirtschaftssoziologie',
  Wirtschaftswissenschaften = 'Wirtschaftswissenschaften',
  Zahnmedizin = 'Zahnmedizin',
  otherMajor = 'otherMajor',
  OkologieundEvolution = 'OkologieundEvolution',
}

export enum NebenFaecherEnum {
  KEIN_NEBENFACH = 'KEIN_NEBENFACH',
  Afrikanistik = 'Afrikanistik',
  AllgundverglLiteraturwissenschaft = 'AllgundverglLiteraturwissenschaft',
  AmericanStudies = 'AmericanStudies',
  AnglophoneLiteraturesCulturesandMedia = 'AnglophoneLiteraturesCulturesandMedia',
  Archaologie = 'Archaologie',
  Archaometrie = 'Archaometrie',
  AudiovisualandCinemaStudies = 'AudiovisualandCinemaStudies',
  Betriebswirtschaftslehre = 'Betriebswirtschaftslehre',
  Biochemie = 'Biochemie',
  Bioinformatik = 'Bioinformatik',
  BiologieLehramt = 'BiologieLehramt',
  Biophysik = 'Biophysik',
  Biowissenschaften = 'Biowissenschaften',
  Chemie = 'Chemie',
  ChemieLehramt = 'ChemieLehramt',
  ComparativeLiteratures = 'ComparativeLiteratures',
  CuratorialStudiesTheorieGeschichteKritik = 'CuratorialStudiesTheorieGeschichteKritik',
  DeutschLehramt = 'DeutschLehramt',
  DeutschPortugiesischeStudienEstudosLusoAlemaes = 'DeutschPortugiesischeStudienEstudosLusoAlemaes',
  DeutscheLiteratur = 'DeutscheLiteratur',
  Dramaturgie = 'Dramaturgie',
  EmpirischeSprachwissenschaften = 'EmpirischeSprachwissenschaften',
  EnglischLehramt = 'EnglischLehramt',
  EnglishStudies = 'EnglishStudies',
  ErdkundeLehramt = 'ErdkundeLehramt',
  Erziehungswissenschaften = 'Erziehungswissenschaften',
  Ethnologie = 'Ethnologie',
  EvangelischeReligionLehramt = 'EvangelischeReligionLehramt',
  EvangelischeTheologie = 'EvangelischeTheologie',
  FilmkulturArchivierungProgrammierungPrasentation = 'FilmkulturArchivierungProgrammierungPrasentation',
  FranzosischLehramt = 'FranzosischLehramt',
  GenderStudies = 'GenderStudies',
  Geographie = 'Geographie',
  GeographienderGlobalisierungMarkteundMetropolen = 'GeographienderGlobalisierungMarkteundMetropolen',
  Geowissenschaften = 'Geowissenschaften',
  Germanistik = 'Germanistik',
  Geschichte = 'Geschichte',
  GeschichteLehramt = 'GeschichteLehramt',
  GeschichteundPhilosophiederWissenschaften = 'GeschichteundPhilosophiederWissenschaften',
  GriechischLehramt = 'GriechischLehramt',
  GriechischePhilologie = 'GriechischePhilologie',
  Informatik = 'Informatik',
  InformatikLehramt = 'InformatikLehramt',
  InterdisciplinaryNeuroscience = 'InterdisciplinaryNeuroscience',
  InternationalEconomicsandEconomicPolicy = 'InternationalEconomicsandEconomicPolicy',
  InternationalManagement = 'InternationalManagement',
  InternationaleStudienFriedensundKonfliktforschung = 'InternationaleStudienFriedensundKonfliktforschung',
  IslamischeReligionLehramt = 'IslamischeReligionLehramt',
  IslamischeStudien = 'IslamischeStudien',
  ItalienischLehramt = 'ItalienischLehramt',
  ItalienstudienStudiItaliani = 'ItalienstudienStudiItaliani',
  JapaninderWeltGlobaleHerausforderungenundkulturellePerspektiven = 'JapaninderWeltGlobaleHerausforderungenundkulturellePerspektiven',
  Japanologie = 'Japanologie',
  Judaistik = 'Judaistik',
  KatholischeReligionLehramt = 'KatholischeReligionLehramt',
  KatholischeTheologie = 'KatholischeTheologie',
  Koreastudien = 'Koreastudien',
  KulturanthropologieundEuropaischeEthnologie = 'KulturanthropologieundEuropaischeEthnologie',
  KunstLehramt = 'KunstLehramt',
  KunstMedienKulturelleBildung = 'KunstMedienKulturelleBildung',
  Kunstgeschichte = 'Kunstgeschichte',
  LateinLehramt = 'LateinLehramt',
  LateinischePhilologie = 'LateinischePhilologie',
  Linguistics = 'Linguistics',
  Linguistik = 'Linguistik',
  Mathematik = 'Mathematik',
  MathematikLehramt = 'MathematikLehramt',
  Medizin = 'Medizin',
  Medizintechnik = 'Medizintechnik',
  Meteorologie = 'Meteorologie',
  ModernEastAsianStudies = 'ModernEastAsianStudies',
  MolekulareBiotechnologie = 'MolekulareBiotechnologie',
  MolekulareBiowissenschaften = 'MolekulareBiowissenschaften',
  MolekulareMedizin = 'MolekulareMedizin',
  MoneyandFinance = 'MoneyandFinance',
  MovingCulturesTransculturalEncounters = 'MovingCulturesTransculturalEncounters',
  MusikLehramt = 'MusikLehramt',
  Musikwissenschaft = 'Musikwissenschaft',
  Pharmazie = 'Pharmazie',
  Philosophie = 'Philosophie',
  PhilosophieLehramt = 'PhilosophieLehramt',
  PhysicalBiologyofCellsandCellInteractions = 'PhysicalBiologyofCellsandCellInteractions',
  Physik = 'Physik',
  PhysikLehramt = 'PhysikLehramt',
  PhysischeGeographie = 'PhysischeGeographie',
  PolitikundWirtschaftLehramt = 'PolitikundWirtschaftLehramt',
  Politikwissenschaft = 'Politikwissenschaft',
  PolitischeTheorie = 'PolitischeTheorie',
  Psychologie = 'Psychologie',
  Rechtswissenschaft = 'Rechtswissenschaft',
  Religionsphilosophie = 'Religionsphilosophie',
  Religionswissenschaft = 'Religionswissenschaft',
  ReligionswissenschaftIslamischeReligion = 'ReligionswissenschaftIslamischeReligion',
  ReligionswissenschaftVergleichendeReligionswissenschaft = 'ReligionswissenschaftVergleichendeReligionswissenschaft',
  Romanistik = 'Romanistik',
  RomanistischeLinguistik = 'RomanistischeLinguistik',
  RomanistischeLiteraturundKulturwissenschaft = 'RomanistischeLiteraturundKulturwissenschaft',
  SachunterrichtLehramt = 'SachunterrichtLehramt',
  Sinologie = 'Sinologie',
  Skandinavistik = 'Skandinavistik',
  SoutheastAsianStudies = 'SoutheastAsianStudies',
  SozialwissenschaftendesSports = 'SozialwissenschaftendesSports',
  Soziologie = 'Soziologie',
  SpanischLehramt = 'SpanischLehramt',
  SportLehramt = 'SportLehramt',
  SportmedizinischesTrainingLeistungsphysiologie = 'SportmedizinischesTrainingLeistungsphysiologie',
  Sportwissenschaft = 'Sportwissenschaft',
  SprachenundKulturenSudostasiens = 'SprachenundKulturenSudostasiens',
  TheaterFilmuMedienwissenschaft = 'TheaterFilmuMedienwissenschaft',
  Umweltwissenschaften = 'Umweltwissenschaften',
  VoruFruhgeschichtlicheArchaologie = 'VoruFruhgeschichtlicheArchaologie',
  WirtschaftsundFinanzsoziologie = 'WirtschaftsundFinanzsoziologie',
  Wirtschaftsinformatik = 'Wirtschaftsinformatik',
  Wirtschaftspadagogik = 'Wirtschaftspadagogik',
  Wirtschaftssoziologie = 'Wirtschaftssoziologie',
  Wirtschaftswissenschaften = 'Wirtschaftswissenschaften',
  Zahnmedizin = 'Zahnmedizin',
  otherMajor = 'otherMajor',
  OkologieundEvolution = 'OkologieundEvolution',
}

export enum Semester {
  FIRST = 'FIRST',
  SECOND = 'SECOND',
  THIRD = 'THIRD',
  FOURTH = 'FOURTH',
  FIFTH = 'FIFTH',
  SIXTH = 'SIXTH',
  SEVENTH = 'SEVENTH',
  EIGHTH = 'EIGHTH',
}

export enum PreferredLanguage {
  GERMAN = 'GERMAN',
  GERMAN_ENGLISH = 'GERMAN_ENGLISH',
  ENGLISH = 'ENGLISH',
}

export enum AngestrebterAbschlussEnum {
  BA = 'BA',
  BEd = 'BEd',
  BEng = 'BEng',
  BFA = 'BFA',
  BSc = 'BSc',
  LLB = 'LLB',
  LLM = 'LLM',
  Lehramt = 'Lehramt',
  MA = 'MA',
  MEd = 'MEd',
  MEng = 'MEng',
  MFA = 'MFA',
  MSc = 'MSc',
  Staatsexamen = 'Staatsexamen',
}

export enum NaechsteKarriereschritteEnum {
  BERUFSEINSTIEG = 'BERUFSEINSTIEG',
  PRAKTIKUM = 'PRAKTIKUM',
  WERKSTUDENTENTAETIGKEIT = 'WERKSTUDENTENTAETIGKEIT',
  PROMOTION_PHD = 'PROMOTION_PHD',
  SELBSTSTAENDIGKEIT_FREIEBERUFLICH_TATIGKEIT = 'SELBSTSTAENDIGKEIT_FREIEBERUFLICH_TATIGKEIT',
  AUSLANDERFAHRUNG = 'AUSLANDERFAHRUNG',
  SONSTIGES = 'SONSTIGES',
}

export enum BevorzugEnum {
  BEVORZUGT = 'BEVORZUGT',
  OPTIONAL = 'OPTIONAL',
  NEIN = 'NEIN',
}

export const GenderTitles = {
  [Gender.MALE]: 'Herr',
  [Gender.FEMALE]: 'Frau',
};

registerEnum({ enum: ConfirmEnum, name: 'ConfirmEnum' });
registerEnum({ enum: ConfirmOrKeinEnum, name: 'ConfirmOrKeinEnum' });
registerEnum({ enum: MeetingType, name: 'MeetingType' });
registerEnum({ enum: FirstTimeEnum, name: 'FirstTimeEnum' });
registerEnum({ enum: NeededFromMentorEnum, name: 'NeededFromMentorEnum' });
registerEnum({ enum: FachbereichEnum, name: 'FachbereichEnum' });
registerEnum({ enum: Semester, name: 'Semester' });
registerEnum({ enum: PreferredLanguage, name: 'PreferredLanguage' });
registerEnum({ enum: HauptFaecherEnum, name: 'HauptFaecherEnum' });
registerEnum({ enum: NebenFaecherEnum, name: 'NebenFaecherEnum' });
registerEnum({ enum: AngestrebterAbschlussEnum, name: 'AngestrebterAbschlussEnum' });
registerEnum({ enum: NaechsteKarriereschritteEnum, name: 'NaechsteKarriereschritteEnum' });
registerEnum({ enum: BevorzugEnum, name: 'BevorzugEnum' });

@Entity()
export class MentoringMentor extends DefaultMentorUser {
  static override _class = 'MentoringMentor';

  constructor(data: Partial<MentoringMentor>) {
    super(data);
  }

  @validate(nonOptional())
  @Field(type => String)
  firstName: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  lastName: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  emailRepeat: Maybe<string>;

  @validate(nonOptional())
  @Field(type => PreferredLanguage)
  mentorSprache: Maybe<PreferredLanguage>;

  @Field(type => String)
  title: Maybe<string>;

  @validate(nonOptional())
  @Field(type => Date)
  birthday: Maybe<Date>;

  @validate(nonOptional())
  @Field(type => Int)
  age: Maybe<number>;

  @Field(type => String)
  phone: Maybe<string>;

  @Field(type => Address)
  address: Maybe<Address>;

  @validate(nonOptional())
  @Field(type => String)
  studium: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  abschluss: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  currentPosition: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  currentOcupationFocus: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  berufserfahrung: Maybe<string>;

  @Field(type => String)
  sonstigesErfahrungen: Maybe<string>;

  @Field(type => MeetingType)
  meetingType: Maybe<MeetingType>;

  @validate(nonOptional())
  @validate(minLength(200))
  @Field(type => String)
  motivationMentor: Maybe<string>;

  @validate(nonOptional())
  @Field(type => [FachbereichEnum], { default: v => v ?? [] })
  menteeDepartments!: FachbereichEnum[];

  @validate(nonOptional())
  @Field(type => Int)
  maxParallelMentees: Maybe<number>;

  @validate(nonOptional())
  @Field(type => ConfirmEnum)
  international: Maybe<ConfirmEnum>;

  @validate(nonOptional())
  @Field(type => ConfirmEnum)
  goetheStudent: Maybe<ConfirmEnum>;

  @Field(type => String)
  businessProfile: Maybe<string>;

  @Field(type => ConfirmEnum)
  abschlussarbeitBetreuen: Maybe<ConfirmEnum>;

  @Field(type => ConfirmEnum)
  workIntegratedLeaning: Maybe<ConfirmEnum>;

  @Field(type => String)
  workIntegratedLeaningSonstiges: Maybe<string>;

  @validate(nonOptional())
  @Field(type => Boolean)
  privacyPolicy: Maybe<boolean>;

  @validate(nonOptional())
  @Field(type => ConfirmOrKeinEnum)
  internationalMentees: Maybe<ConfirmOrKeinEnum>;

  /**
   * From where the mentee discovered the program
   */
  @Field(type => String)
  discoveredFrom: Maybe<string>;

  @Field(type => ID)
  profilePicture: Maybe<string>;

  @Field(type => ID)
  curriculumVitae: Maybe<string>;

  @Field(type => String, {
    resolve: function (this: MentoringMentor) {
      return () => this.firstName + ' ' + this.lastName;
    },
  })
  fullName: Maybe<string>;

  @Field(type => Boolean)
  hiddenFromMentees: Maybe<boolean>;

  static override getMailVariables(user: MentoringMentor) {
    const salutation = [MailTemplateVars.SALUTATION, GenderTitles[user.gender ?? ''] ?? ''];
    return [...super.getMailVariables(user), salutation];
  }
}

@Entity()
export class MentoringMentee extends DefaultMenteeUser {
  static override _class = 'MentoringMentee';

  constructor(data: Partial<MentoringMentee>) {
    super(data);
  }

  @validate(nonOptional())
  @Field(type => String)
  firstName: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  lastName: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  emailRepeat: Maybe<string>;

  @validate(nonOptional())
  @Field(type => ConfirmEnum)
  minSemesterPrerequisite: Maybe<ConfirmEnum>;

  @Field(type => String)
  minSemesterPrerequisiteSonstiges: Maybe<String>;

  @validate(nonOptional())
  @Field(type => ConfirmEnum)
  languageC1: Maybe<ConfirmEnum>;

  @validate(nonOptional())
  @Field(type => Int)
  age: Maybe<number>;

  @validate(nonOptional())
  @Field(type => String)
  staatsangehoerigkeit: Maybe<string>;

  @validate(and(nonOptional(), min(1000000), max(9999999)))
  @Field(type => Int)
  matrikelNummer: Maybe<number>;

  @Field(type => String)
  phone: Maybe<string>;

  @validate(nonOptional())
  @Field(type => Boolean)
  privacyPolicy: Maybe<boolean>;

  @validate(nonOptional())
  @Field(type => PreferredLanguage)
  preferredLanguage: Maybe<PreferredLanguage>;

  @validate(nonOptional())
  @Field(type => [NeededFromMentorEnum], { default: v => v ?? [] })
  whatIsNeededFromMentor: Maybe<NeededFromMentorEnum[]>;

  @Field(type => String)
  whatIsNeededFromMentorSonstiges: Maybe<String>;

  @validate(nonOptional())
  @Field(type => FachbereichEnum)
  department: Maybe<FachbereichEnum>;

  @validate(nonOptional())
  @Field(type => HauptFaecherEnum)
  mainSubject: Maybe<HauptFaecherEnum>;

  @validate(nonOptional())
  @Field(type => ConfirmEnum)
  extraSubject: Maybe<ConfirmEnum>;

  @Field(type => NebenFaecherEnum)
  extraSubjectSonstiges: Maybe<NebenFaecherEnum>;

  @validate(nonOptional())
  @Field(type => Semester)
  menteeSemester: Maybe<Semester>;

  @validate(nonOptional())
  @Field(type => AngestrebterAbschlussEnum)
  angestrebterAbschluss: Maybe<AngestrebterAbschlussEnum>;

  @Field(type => String)
  angestrebterAbschlussSonstiges: Maybe<string>;

  @Field(type => MeetingType)
  meetingType: Maybe<MeetingType>;

  @Field(type => String)
  sonstigesNotes: Maybe<string>;

  @validate(nonOptional())
  @validate(minLength(200))
  @Field(type => String)
  motivationMentee: Maybe<string>;

  @validate(nonOptional())
  @Field(type => String)
  professionField: Maybe<string>;

  @validate(nonOptional())
  @Field(type => NaechsteKarriereschritteEnum)
  naechsteKarriereschritte: Maybe<NaechsteKarriereschritteEnum>;

  @Field(type => String)
  naechsteKarriereschritteSonstiges: Maybe<string>;

  @validate(nonOptional())
  @Field(type => ConfirmOrKeinEnum)
  searchingWork: Maybe<ConfirmOrKeinEnum>;

  @validate(nonOptional())
  @Field(type => Int)
  expectedGraduationYear: Maybe<number>;
  @validate(nonOptional())
  @Field(type => Int)
  expectedGraduationMonth: Maybe<number>;

  @validate(nonOptional())
  @Field(type => ConfirmOrKeinEnum)
  firstGenerationStudent: Maybe<ConfirmOrKeinEnum>;

  @validate(nonOptional())
  @Field(type => ConfirmOrKeinEnum)
  immigrantHistory: Maybe<ConfirmOrKeinEnum>;

  @validate(nonOptional())
  @Field(type => ConfirmEnum)
  studiedAbroad: Maybe<ConfirmEnum>;

  @validate(nonOptional())
  @Field(type => ConfirmEnum)
  erasmusStudent: Maybe<ConfirmEnum>;

  @Field(type => String)
  businessProfile: Maybe<string>;

  @validate(nonOptional())
  @Field(type => FirstTimeEnum)
  firstTime: Maybe<FirstTimeEnum>;

  /**
   * From where the mentee discovered the program
   */
  @Field(type => String)
  discoveredFrom: Maybe<string>;

  @Field(type => ID)
  profilePicture: Maybe<string>;

  @Field(type => ID)
  curriculumVitae: Maybe<string>;

  @Field(type => [ID])
  preferedMentorsIDs: Maybe<string[]>;

  @DataLoadedField(type => [MentoringMentor], mentee => mentee.preferedMentorsIDs ?? [])
  preferedMentors: Maybe<MentoringMentor[]>;

  static override getMailVariables(user: MentoringMentee) {
    const salutation = [MailTemplateVars.SALUTATION, GenderTitles[user.gender ?? Gender.DIVERSE] ?? ''];
    return [...super.getMailVariables(user), salutation];
  }
}

@Entity()
export class MentorFilter extends AdornisFilter {
  static override _class = 'MentorFilter';

  constructor(doc: PartialEntityData<MentorFilter>) {
    super(doc);
  }

  @Field(type => FachbereichEnum)
  department: Maybe<FachbereichEnum>;
  @Field(type => Gender)
  gender: Maybe<Gender>;
  @Field(type => MeetingType)
  meetingType: Maybe<MeetingType>;

  override getMongoFilter() {
    const filter = super.getMongoFilter();
    if (this.department) filter.menteeDepartments = this.department;
    if (this.gender) filter.gender = this.gender;
    if (this.meetingType) filter.meetingType = this.meetingType;
    return filter;
  }
}
